import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '@core/api/useApiClient';

/**
 * Distinct list of accounts the user has trades for. Used to populate filter dropdowns on the
 * Dashboard / PnL / Trades pages. Backed by `GetAccountsHandler` on the BE; cached for a minute
 * because the set rarely changes and every page mount otherwise re-fetches.
 */
/**
 * One account dropdown entry. `name` is the filter key (CQG internal account_id, or 'Manual') used
 * by every account-scoped query; `displayName` is the friendly label (brokerage/account name from
 * the CQG AccountsReport, falling back to `name`).
 */
export interface AccountInfo {
  name: string;
  displayName: string;
  brokerageAccountNumber: string | null;
  /**
   * How the account was discovered (BE `UserAccountProvider`, camelCased by the JSON enum converter).
   * Drives the label prefix + the Accounts panel's provider chip / live matching. Keep in sync with
   * the inline copy in `core/realtime/events.ts` (`accounts.updated`) — core stays feature-import-free.
   */
  provider: 'manual' | 'cqg' | 'imported' | 'rithmic';
  /**
   * The CQG gateway environment the account was discovered under (`demo`/`live`), persisted so an
   * OFFLINE row still shows its DEMO/LIVE tag and the panel's Connect routes to the right tab
   * (2026-07-18). Null for non-CQG accounts and CQG accounts discovered before this was tracked.
   */
  environment: 'demo' | 'live' | null;
}

export function useAccounts() {
  const api = useApiClient();
  return useQuery({
    queryKey: ['trades', 'accounts'] as const,
    queryFn:  () => api.get<AccountInfo[]>('/api/v1/trades/accounts'),
    staleTime: 60_000,
    // Hide the vestigial 'Manual' registry account everywhere (dropdown + AccountInitializer): manual
    // trade entry is disabled for the MVP launch (imports + CQG only), so it's never selectable. A
    // user with only Manual falls to the empty-state "Connect CQG" CTA — exactly the onboarding path.
    select: (list) => list.filter(a => a.provider !== 'manual'),
  });
}

/**
 * Distinct list of symbols the user has traded. Optional `account` arg narrows to symbols
 * traded inside that account — matches the BE handler signature.
 */
export function useSymbols(account?: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['trades', 'symbols', account ?? null] as const,
    queryFn:  () => {
      const qs = account ? `?account=${encodeURIComponent(account)}` : '';
      return api.get<string[]>(`/api/v1/trades/symbols${qs}`);
    },
    staleTime: 60_000,
  });
}
